import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmResizeTextField } from "components/formFields/FrmResizeTextField";
import { FrmSelectFromObjects } from "components/formFields/FrmSelectFromObjects";
import { FrmTextField } from "components/formFields/FrmTextField";
import { FrmChannel } from "components/formFields/frm/FrmChannel";
import { FrmCheckbox } from "components/formFields/frm/FrmCheckbox";
import { FrmCustomer } from "components/formFields/frm/FrmCustomer";
import { FrmLocation } from "components/formFields/frm/FrmLocation";
import { FrmPriority } from "components/formFields/frm/FrmPriority";
import { FrmVendor } from "components/formFields/frm/FrmVendor";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import {
  TRIGGER_FORM_OPTIONS,
  TRIGGER_ACTION_OPTIONS,
} from "services/sosInventoryService/aopRule/schema";
import { getRecord } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import { LineItems } from "views/AopRules/AopRule/LineItems";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function AopRule(props) {
  const { objectType, id, onClose } = props;

  const dispatch = useDispatch();

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  const loadingState = useSelector((state) => state.loading.editModal);

  const numLinesToAdd = useSelector(
    (state) => state.userCompanySettings.settings.numLinesToAdd
  );

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]({ numLinesToAdd });
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [setRecord, id, objectType, dispatch, numLinesToAdd]);

  function handleTriggerFormChange(name, { id }) {
    setRecord((prevRecord) => ({
      ...prevRecord,
      [name]: id,
      triggerAction: null,
      lines: prevRecord.lines.map((line) => ({
        ...line,
        emailTemplateId: 0,
        createFormType: null,
      })),
    }));
  }

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
        lines={record?.lines}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmTextField
                        autoFocus
                        name="name"
                        label={i18n("frmLabel.Name")}
                        onValueBlur={handleInputFieldChange}
                        value={record.name}
                        error={isInError("name")}
                        dataTesting="name"
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmResizeTextField
                        name="description"
                        value={record.description}
                        label={i18n("frmLabel.Description")}
                        width="100%"
                        onValueBlur={handleInputFieldChange}
                        minRows={2}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmCheckbox
                        name="enabled"
                        value={record.enabled}
                        onValueChange={handleInputFieldChange}
                        label={i18n("frmLabel.Enabled")}
                      />
                    </div>
                  </FrmFieldGrid>
                  <VSpace space={2} />
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div style={{ gridColumn: "1 / 2", width: "20rem" }}>
                      <FrmSelectFromObjects
                        name="triggerForm"
                        label={i18n("frmLabel.WhenAAn")}
                        value={
                          record.triggerForm ? { id: record.triggerForm } : null
                        }
                        onValueChange={handleTriggerFormChange}
                        options={TRIGGER_FORM_OPTIONS}
                        disableClearable
                      />
                    </div>
                    <div style={{ gridColumn: "2 / 2", width: "20rem" }}>
                      <FrmSelectFromObjects
                        name="triggerAction"
                        label={i18n("frmLabel.Is")}
                        value={
                          record.triggerAction
                            ? { id: record.triggerAction }
                            : null
                        }
                        onValueChange={(name, { id }) =>
                          handleInputFieldChange(name, id)
                        }
                        options={TRIGGER_ACTION_OPTIONS[record.triggerForm]}
                        error={isInError("triggerAction")}
                        disableClearable
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", width: "20rem" }}>
                      <FrmChannel
                        label={i18n("frmLabel.WithChannel")}
                        onValueChange={handleInputFieldChange}
                        value={record?.channel}
                      />
                    </div>
                    <div style={{ gridColumn: "2 / 2", width: "20rem" }}>
                      <FrmLocation
                        value={record.location}
                        label={i18n("frmLabel.AtLocation")}
                        onValueChange={handleInputFieldChange}
                        disableClearable={false}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", width: "20rem" }}>
                      {record.triggerForm === "purchase order" ? (
                        <FrmVendor
                          maxWidth="20rem"
                          label={i18n("frmLabel.ToVendor")}
                          onValueChange={handleInputFieldChange}
                          value={record?.customer}
                        />
                      ) : (
                        <FrmPriority
                          label={i18n("frmLabel.WithPriority")}
                          onValueChange={handleInputFieldChange}
                          value={record?.priority}
                        />
                      )}
                    </div>
                    <div style={{ gridColumn: "2 / 2", width: "20rem" }}>
                      <FrmCustomer
                        maxWidth="20rem"
                        label={i18n("frmLabel.ForCustomer")}
                        onValueChange={handleInputFieldChange}
                        value={record?.customer}
                      />
                    </div>
                  </FrmFieldGrid>
                  <VSpace space={1} />
                  <LineItems
                    lines={record.lines}
                    triggerForm={record.triggerForm}
                    setRecord={setRecord}
                    errors={errors.line}
                  />
                </Box>
              ) : (
                !isInError("record") && (
                  <FixedLoadingIndicator
                    text={`${i18n("global.Loading")}...`}
                  />
                )
              )}
            </Box>
            <VSpace space={4} />
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
